import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, FormFeedback, Button } from "reactstrap";

// The backend field names (StoreProductWearItToRequest / UpdateProductWearItToRequest)
// that this form has a matching input for — anything outside this set (e.g. product_id)
// has nowhere to render inline and falls back to the general error banner instead.
const KNOWN_FIELDS = ["title", "icon", "media_path"];

// 422 responses reject with the raw Laravel {message, errors: {field: [...]}} payload
// itself (not an AxiosError — see the shared axios interceptor, src/utils/api.jsx, and
// the same convention already relied on by manageHowToStyleVideoModal.jsx). Splits that
// payload into { fieldErrors: {field: message}, generalMessage } so known fields render
// inline against their own input and everything else still surfaces somewhere.
const splitApiError = (err, fallback) => {
  const isStructured = err && typeof err === "object" && !err.isAxiosError && err.errors && typeof err.errors === "object";

  if (!isStructured) {
    return { fieldErrors: {}, generalMessage: err?.response?.data?.message || err?.message || fallback };
  }

  const fieldErrors = {};
  let generalMessage = "";

  Object.entries(err.errors).forEach(([field, messages]) => {
    const first = Array.isArray(messages) && messages.length ? messages[0] : null;
    if (!first) return;
    if (KNOWN_FIELDS.includes(field)) {
      fieldErrors[field] = first;
    } else if (!generalMessage) {
      generalMessage = first;
    }
  });

  if (!generalMessage && Object.keys(fieldErrors).length === 0) {
    generalMessage = err.message || fallback;
  }

  return { fieldErrors, generalMessage };
};

const isVideoFile = (file) => Boolean(file && file.type && file.type.startsWith("video/"));

const EMPTY_FORM = { title: "" };
const EMPTY_FIELD_ERRORS = { title: "", icon: "", media_path: "" };

/**
 * Single reusable create/edit form for a "Wear it to" entry — Title, an uploaded icon
 * image, and one image-or-video asset with a preview. Mirrors the field wiring,
 * required-title gating and submit/close behaviour of manageSignatureDetailsModal.jsx
 * (GOD-TASK-213) — including its icon-as-uploaded-image handling, which this now matches
 * — and the single-file-with-ref-reset + video/image preview handling of
 * manageHowToStyleVideoModal.jsx. (GOD-TASK-231 was checked for a prior "Wear it to"
 * implementation to reuse from, per instruction, but no such task/commit exists anywhere
 * in this repo's history — there is nothing there to reuse.)
 *
 * This component still never calls the API directly — `onSubmit` is a caller-supplied
 * async function (the parent, manageWearItToModal.jsx, wires it to the real Phase 4
 * create/update endpoints) that receives the validated { title, iconFile, mediaFile } and
 * is awaited purely to drive the in-flight/disabled state and surface any rejection as
 * field-level errors against the matching input (falls back to a general banner for
 * anything that isn't title/icon/media_path, e.g. a product_id error).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - mode: "create" | "edit"
 *  - record: the existing row when mode === "edit" ({ title, icon, media_path, media_type }) — icon and
 *    media_path are both expected pre-resolved to full URLs (ProductWearItTo's
 *    getIconAttribute/getMediaPathAttribute already do this server-side), and media_type
 *    is 1 (image) or 2 (video).
 *  - onSubmit: ({ title, iconFile, mediaFile }) => Promise
 */
const WearItToFormModal = ({ open, onClose, mode, record, onSubmit }) => {
  const isEditing = mode === "edit";

  const [values, setValues] = useState(EMPTY_FORM);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null);
  const [mediaPreviewIsVideo, setMediaPreviewIsVideo] = useState(false);
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);
  const [fieldErrors, setFieldErrors] = useState(EMPTY_FIELD_ERRORS);
  const [generalError, setGeneralError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const mediaInputRef = useRef(null);
  const iconInputRef = useRef(null);
  const mediaObjectUrlRef = useRef(null); // tracks a locally-created blob: URL so it can be revoked
  const iconObjectUrlRef = useRef(null);

  const revokeMediaPreview = () => {
    if (mediaObjectUrlRef.current) {
      URL.revokeObjectURL(mediaObjectUrlRef.current);
      mediaObjectUrlRef.current = null;
    }
  };

  const revokeIconPreview = () => {
    if (iconObjectUrlRef.current) {
      URL.revokeObjectURL(iconObjectUrlRef.current);
      iconObjectUrlRef.current = null;
    }
  };

  const showStoredMediaPreview = () => {
    setMediaPreviewUrl(record?.media_path || record?.media || null);
    setMediaPreviewIsVideo(Number(record?.media_type) === 2);
  };

  const showStoredIconPreview = () => {
    setIconPreviewUrl(record?.icon || null);
  };

  // Reset/pre-populate whenever the modal is (re)opened, and whenever the target record
  // changes — so switching which row is being edited never leaves stale form state.
  useEffect(() => {
    if (!open) return;

    revokeMediaPreview();
    revokeIconPreview();
    if (mediaInputRef.current) mediaInputRef.current.value = "";
    if (iconInputRef.current) iconInputRef.current.value = "";
    setFieldErrors(EMPTY_FIELD_ERRORS);
    setGeneralError("");
    setSubmitting(false);

    if (isEditing && record) {
      setValues({ title: record.title || "" });
      showStoredMediaPreview();
      showStoredIconPreview();
    } else {
      setValues(EMPTY_FORM);
      setMediaPreviewUrl(null);
      setMediaPreviewIsVideo(false);
      setIconPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, record]);

  // Revoke any locally-created object URLs on unmount too.
  useEffect(
    () => () => {
      revokeMediaPreview();
      revokeIconPreview();
    },
    []
  );

  const handleMediaChange = (e) => {
    // Single file only: the input has no `multiple` attribute, so a second selection
    // simply replaces files[0] — no extra replace-logic needed here.
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;

    revokeMediaPreview();

    if (file) {
      const url = URL.createObjectURL(file);
      mediaObjectUrlRef.current = url;
      setMediaPreviewUrl(url);
      setMediaPreviewIsVideo(isVideoFile(file));
    } else if (isEditing && record) {
      // Picker cleared in edit mode: fall back to showing the currently stored asset.
      showStoredMediaPreview();
    } else {
      setMediaPreviewUrl(null);
      setMediaPreviewIsVideo(false);
    }

    setValues((v) => ({ ...v, mediaFile: file }));
  };

  const handleIconChange = (e) => {
    // Same single-file-replaces-previous behaviour as the media input.
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;

    revokeIconPreview();

    if (file) {
      const url = URL.createObjectURL(file);
      iconObjectUrlRef.current = url;
      setIconPreviewUrl(url);
    } else if (isEditing && record) {
      // Picker cleared in edit mode: fall back to showing the currently stored icon.
      showStoredIconPreview();
    } else {
      setIconPreviewUrl(null);
    }

    setValues((v) => ({ ...v, iconFile: file }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;

    const title = values.title.trim();
    const clientErrors = { ...EMPTY_FIELD_ERRORS };

    if (!title) {
      clientErrors.title = "Title is required.";
    }
    // Edit mode: an omitted file retains whatever is already stored (mirrors
    // ProductWearItToController::update preserving media_path/media_type when no file
    // is sent) — so the "exactly one file" gate only applies to create. Icon stays
    // optional in both modes.
    if (!isEditing && !values.mediaFile) {
      clientErrors.media_path = "Please choose an image or video file.";
    }

    if (clientErrors.title || clientErrors.media_path) {
      setFieldErrors(clientErrors);
      setGeneralError("");
      return;
    }

    setFieldErrors(EMPTY_FIELD_ERRORS);
    setGeneralError("");
    setSubmitting(true);

    Promise.resolve(onSubmit({ title, iconFile: values.iconFile || null, mediaFile: values.mediaFile || null }))
      .catch((err) => {
        const { fieldErrors: serverFieldErrors, generalMessage } = splitApiError(err, "Failed to save. Please try again.");
        setFieldErrors({ ...EMPTY_FIELD_ERRORS, ...serverFieldErrors });
        setGeneralError(generalMessage);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal isOpen={open} toggle={onClose} className="modal-dialog-centered" size="md">
      <ModalHeader toggle={onClose} tag="div">
        {isEditing ? "Edit Wear It To" : "Add Wear It To"}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Title</Label>
            <Input
              type="text"
              value={values.title}
              onChange={(e) => setValues((v) => ({ ...v, title: e.target.value }))}
              invalid={Boolean(fieldErrors.title)}
            />
            {fieldErrors.title && <FormFeedback>{fieldErrors.title}</FormFeedback>}
          </FormGroup>

          <FormGroup>
            <Label>
              Icon <small className="text-muted">(optional {isEditing && "— leave blank to keep the current icon"})</small>
            </Label>
            <Input
              innerRef={iconInputRef}
              type="file"
              accept="image/*"
              onChange={handleIconChange}
              invalid={Boolean(fieldErrors.icon)}
            />
            {fieldErrors.icon && <FormFeedback className="d-block">{fieldErrors.icon}</FormFeedback>}
            {iconPreviewUrl && (
              <div style={{ marginTop: 10 }}>
                <img src={iconPreviewUrl} alt="Icon preview" style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 4 }} />
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              Image / Video {isEditing && <small className="text-muted">(leave blank to keep the current file)</small>}
            </Label>
            <Input
              innerRef={mediaInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleMediaChange}
              invalid={Boolean(fieldErrors.media_path)}
            />
            {fieldErrors.media_path && <FormFeedback className="d-block">{fieldErrors.media_path}</FormFeedback>}
            {mediaPreviewUrl && (
              <div style={{ marginTop: 10 }}>
                {mediaPreviewIsVideo ? (
                  <video src={mediaPreviewUrl} controls style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 4, background: "#000" }} />
                ) : (
                  <img
                    src={mediaPreviewUrl}
                    alt="Preview"
                    style={{ maxWidth: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 4 }}
                  />
                )}
              </div>
            )}
          </FormGroup>

          {generalError && <div className="text-danger small mb-2">{generalError}</div>}

          <div className="d-flex" style={{ gap: 8 }}>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
            <Button color="secondary" type="button" outline onClick={onClose}>
              Cancel
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default WearItToFormModal;
